import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface TeamProtectedRouteProps {
  children: React.ReactNode;
}

const TEAM_ACCESS_COOKIE = 'team_access';

const TeamProtectedRoute: React.FC<TeamProtectedRouteProps> = ({ children }) => {
  const navigate = useNavigate();

  const getCookie = (name: string): string | null => {
    try {
      const cookies = document.cookie.split(';');
      const cookie = cookies.find(c => c.trim().startsWith(`${name}=`));
      if (!cookie) return null;
      return cookie.split('=')[1].trim();
    } catch (error) {
      console.error('Error reading cookie:', error);
      return null;
    }
  };

  const checkAccess = () => {
    const isRestricted = process.env.REACT_APP_ENABLE_TEAM_RESTRICTION === 'true';
    const accessCookie = getCookie(TEAM_ACCESS_COOKIE);
    const expectedKey = process.env.REACT_APP_TEAM_ACCESS_KEY;

    // Detailed debug logging
    console.group('Access Check Details');
    console.log('Environment Variables:', {
      REACT_APP_ENABLE_TEAM_RESTRICTION: process.env.REACT_APP_ENABLE_TEAM_RESTRICTION,
      REACT_APP_TEAM_ACCESS_KEY: process.env.REACT_APP_TEAM_ACCESS_KEY,
      nodeEnv: process.env.NODE_ENV,
      stage: process.env.REACT_APP_STAGE
    });
    console.log('Cookie Information:', {
      cookieValue: accessCookie,
      allCookies: document.cookie
    });
    console.log('Access State:', {
      isRestricted,
      hasAccessCookie: !!accessCookie,
      hasExpectedKey: !!expectedKey,
      cookieMatchesKey: accessCookie === expectedKey
    });
    console.groupEnd();

    // If restriction is disabled, allow access
    if (!isRestricted) {
      console.log('✅ Access granted: restriction disabled');
      return true;
    }

    // If missing cookie or expected key, deny access
    if (!accessCookie || !expectedKey) {
      console.log('❌ Access denied: missing cookie or key');
      return false;
    }

    // Compare cookie value with expected key
    const isValid = accessCookie === expectedKey;
    console.log(isValid ? '✅ Cookie valid' : '❌ Cookie invalid', {
      cookieValue: accessCookie,
      expectedKey,
      match: isValid
    });

    return isValid;
  };

  useEffect(() => {
    // Initial access check on mount
    const hasAccess = checkAccess();
    if (!hasAccess) {
      console.log('❌ Access denied, redirecting to 404');
      navigate('/not-found', { replace: true });
    }
  }, [navigate]);

  // Perform access check for render
  const hasAccess = checkAccess();

  if (!hasAccess) {
    return null;
  }

  console.log('✅ Access granted, rendering content');
  return <>{children}</>;
};

export default TeamProtectedRoute; 